import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as assetService from "../services/assetService";
import { getBranches } from "../services/branchService";
import { useForm } from "../hooks/useForm";
import "../styles/AssetForm.css";

const AssetForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [assetTypes, setAssetTypes] = useState([]);
  const [branches, setBranches] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(true);

  useEffect(() => {
    loadAssetTypes();
    loadBranches();
    if (isEditMode) {
      loadAsset();
    }
  }, [id]);

  const loadAssetTypes = async () => {
    try {
      const response = await assetService.getAllAssetTypes();
      setAssetTypes(response.data);
    } catch (err) {
      console.error("Failed to load asset types", err);
    } finally {
      setLoadingTypes(false);
    }
  };

  const loadBranches = async () => {
    try {
      setBranches(await getBranches());
    } catch (err) {
      console.error("Failed to load branches", err);
    }
  };

  const loadAsset = async () => {
    try {
      const asset = await assetService.getAssetById(id);

      // Format dates for input fields (YYYY-MM-DD)
      const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return "";
        return date.toISOString().split("T")[0];
      };

      form.setValues({
        assetTypeId: asset.assetTypeId,
        branchId: asset.branchId || "",
        name: asset.name,
        asset_code: asset.asset_code || "",
        status: asset.status,
        location: asset.location || "",
        assignedTo: asset.assignedTo || "",
        description: asset.description || "",
        purchaseDate: formatDate(asset.purchaseDate),
        warrantyExpiration: formatDate(asset.warrantyExpiration),
        cost: asset.cost || "",
        properties: asset.properties || {},
      });
      // Load property definitions for this asset type
      if (asset.assetTypeId) {
        handleAssetTypeChange(asset.assetTypeId);
      }
    } catch (err) {
      console.error("Failed to load asset", err);
    }
  };

  const handleAssetTypeChange = async (assetTypeId) => {
    if (assetTypeId) {
      try {
        const response = await assetService.getPropertiesByAssetType(assetTypeId);
        setProperties(response.data);
      } catch (err) {
        console.error("Failed to load properties", err);
      }
    } else {
      setProperties([]);
    }
  };

  const onSubmit = async (values) => {
    try {
      // Clean up date fields - send null instead of empty string
      const cleanedValues = {
        ...values,
        purchaseDate: values.purchaseDate || null,
        warrantyExpiration: values.warrantyExpiration || null,
        cost: values.cost || null,
      };

      if (isEditMode) {
        await assetService.updateAsset(id, cleanedValues);
        alert("Asset updated successfully");
      } else {
        await assetService.createAsset(cleanedValues);
        alert("Asset created successfully");
      }
      navigate("/assets");
    } catch (err) {
      alert(err.response?.data?.message || "Error saving asset");
      throw err;
    }
  };

  const form = useForm(
    {
      assetTypeId: "",
      branchId: "",
      name: "",
      asset_code: "",
      status: "available",
      location: "",
      assignedTo: "",
      description: "",
      purchaseDate: "",
      warrantyExpiration: "",
      cost: "",
      properties: {},
    },
    onSubmit,
  );

  if (loadingTypes) return <div className="loading">Loading...</div>;

  return (
    <div className="asset-form-container">
      <h1>{isEditMode ? "Edit Asset" : "Create Asset"}</h1>

      <form onSubmit={form.handleSubmit} className="asset-form">
        <div className="form-section">
          <h2>Basic Information</h2>

          <div className="form-group">
            <label htmlFor="assetTypeId">Asset Type *</label>
            <select
              id="assetTypeId"
              name="assetTypeId"
              value={form.values.assetTypeId}
              onChange={(e) => {
                form.handleChange(e);
                handleAssetTypeChange(e.target.value);
              }}
              required
              disabled={isEditMode}
            >
              <option value="">Select Asset Type</option>
              {assetTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="branchId">Branch *</label>
            <select id="branchId" name="branchId" value={form.values.branchId} onChange={form.handleChange} required>
              <option value="">Select Branch</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.branch_code} - {branch.branch_name}
                </option>
              ))}
            </select>
            {!branches.length && <small>Create a branch before creating an asset.</small>}
          </div>

          <div className="form-group">
            <label htmlFor="asset_code">Asset Tag *</label>
            <input type="text" id="asset_code" name="asset_code" value={form.values.asset_code} onChange={form.handleChange} onBlur={form.handleBlur} required />
          </div>

          <div className="form-group">
            <label htmlFor="name">Asset Name *</label>
            <input type="text" id="name" name="name" value={form.values.name} onChange={form.handleChange} onBlur={form.handleBlur} required />
          </div>

          <div className="form-group">
            <label htmlFor="status">Status</label>
            <select id="status" name="status" value={form.values.status} onChange={form.handleChange}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="retired">Retired</option>
              <option value="in-maintenance">In Maintenance</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="location">Location</label>
            <input type="text" id="location" name="location" value={form.values.location} onChange={form.handleChange} />
          </div>

          <div className="form-group">
            <label htmlFor="assignedTo">Assigned To</label>
            <input type="text" id="assignedTo" name="assignedTo" value={form.values.assignedTo} onChange={form.handleChange} />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea id="description" name="description" value={form.values.description} onChange={form.handleChange} rows="4" />
          </div>
        </div>

        {/* <div className="form-section">
          <h2>Financial Information</h2>

          <div className="form-group">
            <label htmlFor="purchaseDate">Purchase Date</label>
            <input
              type="date"
              id="purchaseDate"
              name="purchaseDate"
              value={form.values.purchaseDate}
              onChange={form.handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="warrantyExpiration">Warranty Expiration</label>
            <input
              type="date"
              id="warrantyExpiration"
              name="warrantyExpiration"
              value={form.values.warrantyExpiration}
              onChange={form.handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="cost">Cost</label>
            <input
              type="number"
              id="cost"
              name="cost"
              step="0.01"
              value={form.values.cost}
              onChange={form.handleChange}
            />
          </div>
        </div> */}

        {properties.length > 0 && (
          <div className="form-section">
            <h2>Custom Properties</h2>
            {properties.map((prop) => (
              <div key={prop.id} className="form-group">
                <label htmlFor={`prop-${prop.id}`}>
                  {prop.propertyName}
                  {prop.isRequired && <span className="required">*</span>}
                </label>
                <PropertyInput
                  property={prop}
                  value={form.values.properties[prop.propertyName] || ""}
                  onChange={(value) => {
                    form.setValues((prev) => ({
                      ...prev,
                      properties: {
                        ...prev.properties,
                        [prop.propertyName]: value,
                      },
                    }));
                  }}
                />
              </div>
            ))}
          </div>
        )}

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={form.isSubmitting}>
            {form.isSubmitting ? "Saving..." : "Save"}
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => navigate("/assets")}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

const PropertyInput = ({ property, value, onChange }) => {
  switch (property.dataType) {
    case "integer":
      return <input type="number" value={value} onChange={(e) => onChange(e.target.value)} required={property.isRequired} />;
    case "decimal":
      return <input type="number" step="0.01" value={value} onChange={(e) => onChange(e.target.value)} required={property.isRequired} />;
    case "date":
      return <input type="date" value={value} onChange={(e) => onChange(e.target.value)} required={property.isRequired} />;
    case "boolean":
      return <input type="checkbox" checked={value === "true" || value === true} onChange={(e) => onChange(e.target.checked ? "true" : "false")} />;
    case "text":
      return <textarea value={value} onChange={(e) => onChange(e.target.value)} rows="3" required={property.isRequired} />;
    default:
      return <input type="text" value={value} onChange={(e) => onChange(e.target.value)} required={property.isRequired} />;
  }
};

export default AssetForm;
